import { useMemo } from "react";
import {
  ReservationSummaryEnum,
  type Airline,
  type PackageItinerarySummary,
} from "@b2bportal/rbc-bundled-packages-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import dayjs from "dayjs";
import type { TFunction } from "i18next";
import { packagesPostBookingApi } from "@/modules/packages-postBooking";
import { formatDateWithWeekday } from "@/utilities/formatDate";
export interface UsePackageReservationCardProps {
  reservation: PackageItinerarySummary;
  selectItinerary: () => void;
}

const getProductSummary = (
  reservation: PackageItinerarySummary,
  airlines: { [key: string]: Airline },
  t: TFunction
) => {
  return reservation.reservations.map((r) => {
    if (r.ReservationSummary === ReservationSummaryEnum.Car) {
      return t("core-packages.itinerary.productSummary.car", {
        supplierName: r.supplierName,
      });
    }
    if (r.ReservationSummary === ReservationSummaryEnum.Hotel) {
      return t("core-packages.itinerary.productSummary.hotel", {
        hotelName: r.hotelName,
      });
    }
    if (r.ReservationSummary === ReservationSummaryEnum.Flight) {
      const airlineNames = Array.from(
        new Set(r.carriers.map((c) => airlines[c]?.displayName ?? c))
      ).join(", ");
      return t("core-packages.itinerary.productSummary.flight", {
        airlineName: airlineNames,
      });
    }
  });
};

export const usePackageReservationCard = ({
  reservation,
  selectItinerary,
}: UsePackageReservationCardProps) => {
  const { formatDateTime, t } = useI18nContext();

  // Access airlines directly from RTK Query cache
  const { airlines = {} } =
    packagesPostBookingApi.endpoints.fetchAllPackageReservations.useQuery(
      undefined,
      {
        selectFromResult: ({ data }) => ({
          airlines: data?.context?.airlines ?? {},
        }),
      }
    );

  return useMemo(() => {
    const isValidDate = (date?: string) => date && dayjs(date).isValid();

    const dates =
      isValidDate(reservation.startDate) && isValidDate(reservation.endDate)
        ? `${formatDateWithWeekday(
            dayjs(reservation.startDate).toDate(),
            formatDateTime
          )} - ${formatDateWithWeekday(
            dayjs(reservation.endDate).toDate(),
            formatDateTime
          )}`
        : "";

    return {
      handlers: {
        selectItinerary,
      },
      text: {
        title: reservation.destinationName,
        confirmation: reservation.customerConfirmationId,
        description: getProductSummary(reservation, airlines, t)
          .filter(Boolean)
          .join(" | "),
        dates,
      },
    };
  }, [reservation, formatDateTime, selectItinerary, airlines, t]);
};
